import { get, post } from "./client";

/**
 * Client for the office slug backfill endpoints (SOFTSUPP-333 follow-up). The
 * Address Book generates URL slugs for offices on create, but ~85 offices
 * created before the generator existed have NULL slugs and are invisible to a
 * paid product. The scan previews the slug the generator would produce for
 * each of them; apply writes the slugs for an approved selection of offices.
 *
 * Both endpoints live on address-book and ride the staff-bff discovery proxy,
 * same as its Developer-section sibling, Office Identity Backfill.
 */

/**
 * Office ids per `apply` request. Callers with a bigger batch must split it
 * and send the chunks one after another — the response is the only record of
 * which slug each office actually received, so a single oversized request
 * that timed out would lose the rollback data for the whole run. In practice
 * the backlog is ~85 offices: one chunk.
 */
export const APPLY_CHUNK_SIZE = 100;

export type SkipReason = "no_company_slug" | "unslugifiable";

/**
 * One slugless office. Exactly one of `plannedSlug` / `skipReason` is
 * non-null: either the generator can plan a slug, or the reason it cannot.
 * Rows come ordered by company, then office.
 */
export type ScanRow = {
  officeId: string;
  officeName: string;
  companyId: string;
  companyName: string;
  city: string | null;
  countryName: string | null;
  plannedSlug: string | null;
  skipReason: SkipReason | null;
};

export type ScanCounts = {
  plannable: number;
  skippedNoCompanySlug: number;
  skippedUnslugifiable: number;
};

export type ScanResponse = {
  rows: ScanRow[];
  counts: ScanCounts;
  requestId: string;
};

export type ApplyRowStatus =
  | "generated"
  | "skipped_not_empty"
  | "skipped_no_company_slug"
  | "skipped_not_ab_native"
  | "skipped_unslugifiable"
  | "not_found";

/** One result per submitted office id. */
export type ApplyRowResult = {
  officeId: string;
  officeName: string | null;
  status: ApplyRowStatus;
  /** The slug that was written. Present only on status "generated". */
  slug?: string;
};

export type ApplyCounts = {
  generated: number;
  skipped: number;
  notFound: number;
};

export type ApplyResponse = {
  results: ApplyRowResult[];
  counts: ApplyCounts;
  requestId: string;
};

export const officeSlugBackfillService = {
  async scan(): Promise<ScanResponse> {
    return get<ScanResponse>("/v1/address-book/office-slug-backfill");
  },

  async apply(officeIds: string[]): Promise<ApplyResponse> {
    return post<ApplyResponse>("/v1/address-book/office-slug-backfill/apply", { officeIds });
  },
};
